/**
 * A* shortest-path router over a Hanan grid.
 *
 * Cost model:
 *   f(v) = g(v) + h(v)
 *   g(v) = Σ segment lengths + (turn_penalty × turn_count)
 *   h(v) = Manhattan distance to target (admissible for orthogonal)
 *
 * The turn penalty is what makes routes look CLEAN instead of the
 * squiggly staircases grid-A* normally produces. A path with fewer
 * bends is preferred over a marginally shorter one with more bends.
 *
 * State includes the direction we ENTERED each vertex from, because
 * the turn penalty depends on whether we continue straight or turn.
 */

import type { Point, Rect } from './types';
import { segmentCrossesObstacle, type HananGrid } from './HananGrid';

/** Compass directions on an orthogonal grid. 'S' (start) is used for
 *  the initial state where no direction has been established yet —
 *  any first move is free of turn penalty. */
type Dir = 'N' | 'E' | 'S' | 'W' | 'START';

interface AStarNode {
  xi: number;
  yi: number;
  dir: Dir;
  g: number;
  f: number;
  parent: AStarNode | null;
}

interface RouteRequest {
  fromPoint: Point;
  toPoint: Point;
  /** Direction the route must START moving in (away from source).
   *  Typically determined by the source's port side. */
  fromDir: 'N' | 'E' | 'S' | 'W';
  /** Direction of the FINAL move into target — i.e., edge arrives
   *  from this direction. */
  toDir: 'N' | 'E' | 'S' | 'W';
  /** Rects to ignore as obstacles (source & target). */
  ignoreRects: ReadonlyArray<Rect>;
  turnPenalty: number;
}

export interface RouteResult {
  /** Orthogonal polyline points, absolute coords. Empty if no path. */
  points: number[];
}

export function findOrthogonalRoute(
  grid: HananGrid,
  req: RouteRequest,
): RouteResult {
  const { fromPoint, toPoint, fromDir, toDir, ignoreRects, turnPenalty } = req;

  const sxi = grid.xIndex(fromPoint.x);
  const syi = grid.yIndex(fromPoint.y);
  const txi = grid.xIndex(toPoint.x);
  const tyi = grid.yIndex(toPoint.y);

  if (sxi < 0 || syi < 0 || txi < 0 || tyi < 0) {
    return { points: [] };
  }

  const W = grid.xs.length;
  const H = grid.ys.length;

  // Open-set priority queue — small binary-heap implementation.
  // Closed set keyed by (xi, yi, dir) since we distinguish states
  // by arrival direction (turn cost).
  const open: AStarNode[] = [];
  const pushOpen = (n: AStarNode): void => {
    open.push(n);
    let i = open.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (open[p].f <= open[i].f) break;
      [open[p], open[i]] = [open[i], open[p]];
      i = p;
    }
  };
  const popOpen = (): AStarNode | undefined => {
    if (open.length === 0) return undefined;
    const top = open[0];
    const last = open.pop()!;
    if (open.length > 0) {
      open[0] = last;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < open.length && open[l].f < open[m].f) m = l;
        if (r < open.length && open[r].f < open[m].f) m = r;
        if (m === i) break;
        [open[i], open[m]] = [open[m], open[i]];
        i = m;
      }
    }
    return top;
  };

  const bestG = new Map<number, number>();
  const keyOf = (xi: number, yi: number, dir: Dir): number => {
    // Pack into one int: dir (3 bits) + yi (20 bits) + xi
    const dirCode = dir === 'N' ? 0 : dir === 'E' ? 1 : dir === 'S' ? 2 : dir === 'W' ? 3 : 4;
    return dirCode * W * H + yi * W + xi;
  };

  const heuristic = (xi: number, yi: number): number =>
    Math.abs(grid.xs[xi] - toPoint.x) + Math.abs(grid.ys[yi] - toPoint.y);

  const startH = heuristic(sxi, syi);
  const startNode: AStarNode = {
    xi: sxi, yi: syi, dir: 'START',
    g: 0, f: startH, parent: null,
  };
  pushOpen(startNode);
  bestG.set(keyOf(sxi, syi, 'START'), 0);

  const neighbors: Array<{ dxi: number; dyi: number; dir: 'N' | 'E' | 'S' | 'W' }> = [
    { dxi:  0, dyi: -1, dir: 'N' },
    { dxi:  1, dyi:  0, dir: 'E' },
    { dxi:  0, dyi:  1, dir: 'S' },
    { dxi: -1, dyi:  0, dir: 'W' },
  ];

  while (open.length > 0) {
    const cur = popOpen()!;

    // Arrived at target? Arrival direction must match toDir so the
    // edge enters from the "correct" side of the target port.
    if (cur.xi === txi && cur.yi === tyi && (cur.dir === toDir || cur.dir === 'START')) {
      return { points: reconstructPath(cur, grid) };
    }

    // First move from START must be along `fromDir`.
    const restrictNext: Dir | null = cur.dir === 'START' ? fromDir : null;

    const curG = cur.g;
    const curX = grid.xs[cur.xi];
    const curY = grid.ys[cur.yi];

    // Expand in each of the 4 directions. Because we're on a
    // Hanan grid, each direction steps to the NEXT grid column/row
    // along that axis — not necessarily adjacent to the current
    // cell geometrically but adjacent in grid index.
    for (const n of neighbors) {
      if (restrictNext && restrictNext !== n.dir) continue;

      const nxi = cur.xi + n.dxi;
      const nyi = cur.yi + n.dyi;
      if (nxi < 0 || nxi >= W || nyi < 0 || nyi >= H) continue;

      const nextX = grid.xs[nxi];
      const nextY = grid.ys[nyi];
      // Skip blocked vertex (inside an obstacle), except vertices
      // inside the source/target clearance envelope for this route.
      // The grid is global, but source/target ignoreRects are per-edge;
      // without this exception a short endpoint stub can be trapped
      // inside its own node's visual-clearance zone.
      if (
        grid.blockedVertex[nxi * H + nyi] &&
        !pointInsideIgnoredClearance(nextX, nextY, ignoreRects, grid.visualClearance)
      ) {
        continue;
      }

      // Skip if the segment passes through a blocking obstacle.
      if (segmentCrossesObstacle(grid, curX, curY, nextX, nextY, ignoreRects)) continue;

      const segLen = Math.abs(nextX - curX) + Math.abs(nextY - curY);
      const turnCost =
        cur.dir !== 'START' && cur.dir !== n.dir ? turnPenalty : 0;
      const newG = curG + segLen + turnCost;

      const k = keyOf(nxi, nyi, n.dir);
      const prior = bestG.get(k);
      if (prior !== undefined && prior <= newG) continue;
      bestG.set(k, newG);

      const nNode: AStarNode = {
        xi: nxi, yi: nyi, dir: n.dir,
        g: newG,
        f: newG + heuristic(nxi, nyi),
        parent: cur,
      };
      pushOpen(nNode);
    }
  }

  return { points: [] };
}

function pointInsideIgnoredClearance(
  x: number,
  y: number,
  ignoreRects: ReadonlyArray<Rect>,
  clearance: number,
): boolean {
  for (const r of ignoreRects) {
    if (
      x > r.x - clearance &&
      x < r.x + r.width + clearance &&
      y > r.y - clearance &&
      y < r.y + r.height + clearance
    ) {
      return true;
    }
  }
  return false;
}

function reconstructPath(goal: AStarNode, grid: HananGrid): number[] {
  const pts: Array<[number, number]> = [];
  let cur: AStarNode | null = goal;
  while (cur) {
    pts.push([grid.xs[cur.xi], grid.ys[cur.yi]]);
    cur = cur.parent;
  }
  pts.reverse();

  // Collapse collinear runs so the emitted polyline only turns
  // where it actually bends. A* on a Hanan grid steps one grid line
  // at a time, so consecutive same-direction moves otherwise add
  // redundant points.
  const collapsed: number[] = [];
  for (let i = 0; i < pts.length; i++) {
    const [x, y] = pts[i];
    if (collapsed.length >= 4) {
      const prevX = collapsed[collapsed.length - 2];
      const prevY = collapsed[collapsed.length - 1];
      const prevPrevX = collapsed[collapsed.length - 4];
      const prevPrevY = collapsed[collapsed.length - 3];
      const dxA = prevX - prevPrevX;
      const dyA = prevY - prevPrevY;
      const dxB = x - prevX;
      const dyB = y - prevY;
      // Same direction → replace the middle point.
      if (
        (dxA === 0 && dxB === 0 && Math.sign(dyA) === Math.sign(dyB)) ||
        (dyA === 0 && dyB === 0 && Math.sign(dxA) === Math.sign(dxB))
      ) {
        collapsed[collapsed.length - 2] = x;
        collapsed[collapsed.length - 1] = y;
        continue;
      }
    }
    collapsed.push(x, y);
  }
  return collapsed;
}
